import { useState, useEffect, useMemo } from 'react';
import {
  X,
  CheckCircle,
  Banknote,
  CreditCard,
  Clock,
  Loader2,
  AlertCircle,
  ShoppingBag,
  Coins,
  Info,
} from 'lucide-react';
import { formatMoney, getCurrencySymbol, roundMoney, getActiveCurrency } from '@/utils/currency';
import { getCashNotesForCurrency } from '@/data/locales';
import { posApi } from '@/services/api';
import { completeSale, isLocalFirstMode, companyDb } from '@/offline';
import { useShiftStore } from '@/store/shiftStore';
import toast from 'react-hot-toast';

export interface QuotationQuickSaleProps {
  isOpen: boolean;
  onClose: () => void;
  quotation: {
    name: string;
    customer?: string;
    customer_name?: string;
    net_total?: number;
    total?: number;
    grand_total?: number;
    discount_amount?: number;
    tax_amount?: number;
    write_off_amount?: number;
    items?: any[];
  } | null;
  onSuccess: (invoiceName: string) => void;
}

type PaymentMethodType = 'Cash' | 'Bank' | 'Split' | 'Credit';

export default function QuotationQuickSaleModal({
  isOpen,
  onClose,
  quotation,
  onSuccess,
}: QuotationQuickSaleProps) {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodType>('Cash');
  const [cashAmount, setCashAmount] = useState<string>('');
  const [bankAmount, setBankAmount] = useState<string>('');
  const [referenceNo, setReferenceNo] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const openShift = useShiftStore((s) => s.openShift);

  const [loadedItems, setLoadedItems] = useState<any[]>(() => quotation?.items || []);
  const [isLoadingItems, setIsLoadingItems] = useState<boolean>(false);
  const [fetchedDetails, setFetchedDetails] = useState<any>(null);

  // Auto-fetch full quotation details if items are missing on the initial quote object
  useEffect(() => {
    let isMounted = true;
    if (isOpen && quotation?.name) {
      if (quotation.items && quotation.items.length > 0) {
        setLoadedItems(quotation.items);
        setIsLoadingItems(false);
      } else {
        setIsLoadingItems(true);
        const loadQuoteDetails = async () => {
          try {
            if (isLocalFirstMode() || !navigator.onLine) {
              const localQ = await companyDb().local_quotations.get(quotation.name);
              if (isMounted && localQ) {
                if (localQ.items && localQ.items.length > 0) {
                  setLoadedItems(localQ.items);
                }
                setFetchedDetails(localQ);
              }
            } else {
              const res = await posApi.getQuotation(quotation.name);
              const data = res.data?.message || res.data;
              if (isMounted && data) {
                if (data.items && data.items.length > 0) {
                  setLoadedItems(data.items);
                }
                setFetchedDetails(data);
              }
            }
          } catch (e) {
            console.error('Failed to load quotation items:', e);
          } finally {
            if (isMounted) setIsLoadingItems(false);
          }
        };
        loadQuoteDetails();
      }
    }
    return () => {
      isMounted = false;
    };
  }, [isOpen, quotation?.name, quotation?.items]);

  const displayItems = loadedItems.length > 0 ? loadedItems : quotation?.items || [];

  const totalAmount = Number(
    fetchedDetails?.net_total ??
      fetchedDetails?.grand_total ??
      fetchedDetails?.total ??
      quotation?.net_total ??
      quotation?.grand_total ??
      quotation?.total ??
      0
  );
  const customerName =
    fetchedDetails?.customer_name ||
    fetchedDetails?.customer ||
    quotation?.customer_name ||
    quotation?.customer ||
    'Walk-in Customer';
  const currencySymbol = getCurrencySymbol();
  const activeCurrency = getActiveCurrency();

  // Reset and prefill when opened or quotation changes
  useEffect(() => {
    if (isOpen && quotation) {
      setPaymentMethod('Cash');
      setCashAmount(totalAmount > 0 ? String(roundMoney(totalAmount)) : '0');
      setBankAmount('');
      setReferenceNo('');
    }
  }, [isOpen, quotation?.name, totalAmount]);

  // Handle switching payment method
  const handleSelectMethod = (method: PaymentMethodType) => {
    setPaymentMethod(method);
    if (method === 'Cash') {
      if (!cashAmount || Number(cashAmount) <= 0) {
        setCashAmount(totalAmount > 0 ? String(roundMoney(totalAmount)) : '0');
      }
      setBankAmount('');
    } else if (method === 'Bank') {
      if (!bankAmount || Number(bankAmount) <= 0) {
        setBankAmount(totalAmount > 0 ? String(roundMoney(totalAmount)) : '0');
      }
      setCashAmount('');
    } else if (method === 'Split') {
      if (!cashAmount && !bankAmount) {
        setCashAmount(String(roundMoney(totalAmount)));
        setBankAmount('');
      }
    } else if (method === 'Credit') {
      setCashAmount('');
      setBankAmount('');
    }
  };

  // Calculations
  const numCash = Math.max(0, Number(cashAmount) || 0);
  const numBank = Math.max(0, Number(bankAmount) || 0);

  let totalPaid = 0;
  let cashChange = 0;
  let remainingBalance = 0;

  if (paymentMethod === 'Cash') {
    totalPaid = Math.min(numCash, totalAmount);
    cashChange = Math.max(0, roundMoney(numCash - totalAmount));
    remainingBalance = Math.max(0, roundMoney(totalAmount - totalPaid));
  } else if (paymentMethod === 'Bank') {
    totalPaid = Math.min(numBank, totalAmount);
    remainingBalance = Math.max(0, roundMoney(totalAmount - totalPaid));
  } else if (paymentMethod === 'Split') {
    const rawTotal = numCash + numBank;
    totalPaid = Math.min(rawTotal, totalAmount);
    cashChange = Math.max(0, roundMoney(rawTotal - totalAmount));
    remainingBalance = Math.max(0, roundMoney(totalAmount - totalPaid));
  } else {
    // Credit
    totalPaid = 0;
    remainingBalance = totalAmount;
  }

  const isFullyPaid =
    remainingBalance <= 0.005 && (totalPaid >= totalAmount - 0.005 || totalAmount <= 0);
  const isPartiallyPaid = totalPaid > 0.005 && remainingBalance > 0.005;
  const isUnpaid = totalPaid <= 0.005;

  // Suggested cash note amounts (e.g. £50 note for a £49.95 total)
  const suggestedCashNotes = useMemo(() => {
    if (totalAmount <= 0) return [];
    const notes = getCashNotesForCurrency(activeCurrency);
    // Find next 2 note amounts >= totalAmount
    const higherNotes = notes.filter((n) => n >= totalAmount).slice(0, 3);
    return higherNotes;
  }, [totalAmount, activeCurrency]);

  if (!isOpen || !quotation) return null;

  const handleConfirmSale = async () => {
    setIsSubmitting(true);
    try {
      let createdInvoiceName = '';

      // Prepare payments breakdown
      const payments: Array<{ payment_method: string; amount: number; reference_no?: string }> = [];

      if (paymentMethod === 'Cash') {
        const amt = Math.min(numCash, totalAmount);
        if (amt > 0) {
          payments.push({ payment_method: 'Cash', amount: amt });
        }
      } else if (paymentMethod === 'Bank') {
        const amt = Math.min(numBank, totalAmount);
        if (amt > 0) {
          payments.push({
            payment_method: 'Bank',
            amount: amt,
            reference_no: referenceNo.trim() || undefined,
          });
        }
      } else if (paymentMethod === 'Split') {
        let cAmt = numCash;
        let bAmt = numBank;
        if (cAmt + bAmt > totalAmount) {
          if (bAmt >= totalAmount) {
            bAmt = totalAmount;
            cAmt = 0;
          } else {
            cAmt = roundMoney(totalAmount - bAmt);
          }
        }
        if (cAmt > 0) {
          payments.push({ payment_method: 'Cash', amount: cAmt });
        }
        if (bAmt > 0) {
          payments.push({
            payment_method: 'Bank',
            amount: bAmt,
            reference_no: referenceNo.trim() || undefined,
          });
        }
      }
      // If Credit (Pay Later), payments remains []

      const isOfflineOrLocal = isLocalFirstMode() || !navigator.onLine;

      if (isOfflineOrLocal) {
        // Build items for completeSale using displayItems
        const items = displayItems.map((i: any) => ({
          item: i.item,
          item_name: i.item_name,
          item_type: i.item_type || 'Product',
          quantity: Number(i.quantity || i.qty || 1),
          rate: Number(i.rate || 0),
          amount: Number(i.amount || Number(i.rate || 0) * Number(i.quantity || i.qty || 1)),
          tax_rate: i.tax_rate != null ? Number(i.tax_rate) : undefined,
          variant: i.variant,
          variant_name: i.variant_name,
          batch: i.batch,
          batch_no: i.batch_no,
          serial: i.serial,
        }));

        const saleRes = await completeSale({
          customer: fetchedDetails?.customer || quotation.customer || '',
          customerName,
          items,
          discount_amount: Number(
            fetchedDetails?.discount_amount ?? quotation.discount_amount ?? 0
          ),
          tax_amount: Number(fetchedDetails?.tax_amount ?? quotation.tax_amount ?? 0),
          write_off_amount: Number(
            fetchedDetails?.write_off_amount ?? quotation.write_off_amount ?? 0
          ),
          payments,
          pos_shift: openShift?.name,
          notes: `From Quotation ${quotation.name}`,
        });

        createdInvoiceName = saleRes.name;

        // Update local quotation status
        try {
          const db = companyDb();
          await db.local_quotations.update(quotation.name, { status: 'Converted' });
        } catch {
          // Ignore local quotation update errors
        }
      } else {
        // Online API call
        const res = await posApi.convertQuotation({
          quotation_id: quotation.name,
          payment_method:
            paymentMethod === 'Credit'
              ? 'Credit'
              : payments.length > 1
              ? 'Partial'
              : payments[0]?.payment_method || 'Credit',
          pos_shift: openShift?.name,
          payments,
        });

        const data = res.data?.message || res.data;
        createdInvoiceName = data?.invoice_name || data?.name || '';
      }

      if (isFullyPaid) {
        toast.success(
          createdInvoiceName
            ? `Invoice ${createdInvoiceName} created & paid in full!`
            : 'Quotation converted and paid in full!'
        );
      } else if (isPartiallyPaid) {
        toast.success(
          createdInvoiceName
            ? `Invoice ${createdInvoiceName} created with partial payment of ${formatMoney(totalPaid)}!`
            : `Quotation converted with partial payment of ${formatMoney(totalPaid)}!`
        );
      } else {
        toast.success(
          createdInvoiceName
            ? `Invoice ${createdInvoiceName} created as Unpaid (Pay Later)!`
            : 'Quotation converted to Unpaid sale invoice!'
        );
      }

      onSuccess(createdInvoiceName);
      onClose();
    } catch (err: any) {
      console.error('Failed to convert quotation to sale:', err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.exception ||
        err?.message ||
        'Failed to create invoice from quotation';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-150">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden border border-gray-100 flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 shadow-sm">
              <ShoppingBag className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 leading-tight">Create Sale Invoice</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Direct sale for Quotation <span className="font-semibold text-gray-700">{quotation.name}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="p-6 space-y-4 overflow-y-auto">
          {/* Summary Card with Line Items Preview */}
          <div className="bg-gradient-to-br from-indigo-50/70 via-indigo-50/30 to-purple-50/40 border border-indigo-100 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-medium text-indigo-700 uppercase tracking-wider">Customer</div>
                <div className="text-base font-semibold text-gray-900 mt-0.5 truncate max-w-[200px]">
                  {customerName}
                </div>
                <div className="text-xs text-gray-500 mt-1 flex items-center gap-1.5">
                  {isLoadingItems ? (
                    <span className="flex items-center gap-1 text-indigo-600 font-medium">
                      <Loader2 className="w-3 h-3 animate-spin" /> Loading items...
                    </span>
                  ) : (
                    <span>
                      {displayItems.length} line {displayItems.length === 1 ? 'item' : 'items'}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs font-medium text-indigo-700 uppercase tracking-wider">Invoice Total</div>
                <div className="text-2xl font-extrabold text-indigo-700 mt-0.5">
                  {formatMoney(totalAmount)}
                </div>
              </div>
            </div>

            {/* Line items preview */}
            {displayItems.length > 0 && (
              <div className="mt-3 pt-2.5 border-t border-indigo-100/70 max-h-24 overflow-y-auto space-y-1.5 pr-1">
                {displayItems.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs text-gray-700">
                    <span className="truncate max-w-[240px] font-medium" title={item.item_name || item.item}>
                      {item.item_name || item.item}
                    </span>
                    <span className="text-gray-500 font-mono text-[11px] shrink-0 ml-2">
                      {Number(item.quantity || item.qty || 1)} × {formatMoney(Number(item.rate || 0))}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Payment Method Selector (4 tabs) */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Payment Method
              </label>
              <span className="text-[11px] text-gray-400">Select payment arrangement</span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {/* Cash */}
              <button
                type="button"
                onClick={() => handleSelectMethod('Cash')}
                disabled={isSubmitting}
                className={`relative flex flex-col items-center justify-center p-2.5 rounded-xl border-2 text-center transition-all ${
                  paymentMethod === 'Cash'
                    ? 'border-indigo-600 bg-indigo-50/70 shadow-sm text-indigo-900 ring-2 ring-indigo-500/20'
                    : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-700'
                }`}
              >
                <Banknote
                  className={`w-5 h-5 mb-1 ${paymentMethod === 'Cash' ? 'text-indigo-600' : 'text-gray-500'}`}
                />
                <span className="text-xs font-bold">Cash</span>
                <span
                  className={`text-[10px] mt-0.5 font-medium leading-tight ${
                    paymentMethod === 'Cash'
                      ? isFullyPaid
                        ? 'text-emerald-700'
                        : isPartiallyPaid
                        ? 'text-amber-700'
                        : 'text-indigo-600'
                      : 'text-gray-400'
                  }`}
                >
                  {paymentMethod === 'Cash'
                    ? isFullyPaid
                      ? 'Full'
                      : isPartiallyPaid
                      ? 'Partial'
                      : 'Unpaid'
                    : 'Cash'}
                </span>
                {paymentMethod === 'Cash' && (
                  <div className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-indigo-600" />
                )}
              </button>

              {/* Bank / Card */}
              <button
                type="button"
                onClick={() => handleSelectMethod('Bank')}
                disabled={isSubmitting}
                className={`relative flex flex-col items-center justify-center p-2.5 rounded-xl border-2 text-center transition-all ${
                  paymentMethod === 'Bank'
                    ? 'border-indigo-600 bg-indigo-50/70 shadow-sm text-indigo-900 ring-2 ring-indigo-500/20'
                    : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-700'
                }`}
              >
                <CreditCard
                  className={`w-5 h-5 mb-1 ${paymentMethod === 'Bank' ? 'text-indigo-600' : 'text-gray-500'}`}
                />
                <span className="text-xs font-bold">Bank/Card</span>
                <span
                  className={`text-[10px] mt-0.5 font-medium leading-tight ${
                    paymentMethod === 'Bank'
                      ? isFullyPaid
                        ? 'text-emerald-700'
                        : isPartiallyPaid
                        ? 'text-amber-700'
                        : 'text-indigo-600'
                      : 'text-gray-400'
                  }`}
                >
                  {paymentMethod === 'Bank'
                    ? isFullyPaid
                      ? 'Full'
                      : isPartiallyPaid
                      ? 'Partial'
                      : 'Unpaid'
                    : 'Card/Wire'}
                </span>
                {paymentMethod === 'Bank' && (
                  <div className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-indigo-600" />
                )}
              </button>

              {/* Split (Cash + Card) */}
              <button
                type="button"
                onClick={() => handleSelectMethod('Split')}
                disabled={isSubmitting}
                className={`relative flex flex-col items-center justify-center p-2.5 rounded-xl border-2 text-center transition-all ${
                  paymentMethod === 'Split'
                    ? 'border-indigo-600 bg-indigo-50/70 shadow-sm text-indigo-900 ring-2 ring-indigo-500/20'
                    : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-700'
                }`}
              >
                <Coins
                  className={`w-5 h-5 mb-1 ${paymentMethod === 'Split' ? 'text-indigo-600' : 'text-gray-500'}`}
                />
                <span className="text-xs font-bold">Split</span>
                <span
                  className={`text-[10px] mt-0.5 font-medium leading-tight ${
                    paymentMethod === 'Split'
                      ? isFullyPaid
                        ? 'text-emerald-700'
                        : isPartiallyPaid
                        ? 'text-amber-700'
                        : 'text-indigo-600'
                      : 'text-gray-400'
                  }`}
                >
                  {paymentMethod === 'Split'
                    ? isFullyPaid
                      ? 'Full'
                      : isPartiallyPaid
                      ? 'Partial'
                      : 'Split'
                    : 'Cash+Card'}
                </span>
                {paymentMethod === 'Split' && (
                  <div className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-indigo-600" />
                )}
              </button>

              {/* Pay Later / Credit */}
              <button
                type="button"
                onClick={() => handleSelectMethod('Credit')}
                disabled={isSubmitting}
                className={`relative flex flex-col items-center justify-center p-2.5 rounded-xl border-2 text-center transition-all ${
                  paymentMethod === 'Credit'
                    ? 'border-indigo-600 bg-indigo-50/70 shadow-sm text-indigo-900 ring-2 ring-indigo-500/20'
                    : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-700'
                }`}
              >
                <Clock
                  className={`w-5 h-5 mb-1 ${paymentMethod === 'Credit' ? 'text-indigo-600' : 'text-gray-500'}`}
                />
                <span className="text-xs font-bold">Pay Later</span>
                <span
                  className={`text-[10px] mt-0.5 font-medium leading-tight ${
                    paymentMethod === 'Credit' ? 'text-indigo-600' : 'text-gray-400'
                  }`}
                >
                  Unpaid
                </span>
                {paymentMethod === 'Credit' && (
                  <div className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-indigo-600" />
                )}
              </button>
            </div>
          </div>

          {/* Payment Input & Details Area */}
          <div className="bg-slate-50/80 border border-slate-200/80 rounded-xl p-3.5 space-y-3">
            {/* CASH MODE */}
            {paymentMethod === 'Cash' && (
              <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-700">Cash Received / Paid</label>
                  <button
                    type="button"
                    onClick={() => setCashAmount(String(roundMoney(totalAmount)))}
                    className="text-xs text-indigo-600 hover:text-indigo-700 font-semibold hover:underline"
                  >
                    Exact Amount ({formatMoney(totalAmount)})
                  </button>
                </div>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-gray-400 font-semibold text-sm">
                    {currencySymbol}
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={cashAmount}
                    onChange={(e) => setCashAmount(e.target.value)}
                    placeholder="0.00"
                    disabled={isSubmitting}
                    className="w-full pl-9 pr-3 py-2 text-base font-bold text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                {/* Quick note buttons if available */}
                {suggestedCashNotes.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
                    <span className="text-[11px] text-gray-500 mr-1">Quick Notes:</span>
                    {suggestedCashNotes.map((note) => (
                      <button
                        key={note}
                        type="button"
                        onClick={() => setCashAmount(String(note))}
                        className={`px-2 py-0.5 rounded text-xs font-semibold border transition-colors ${
                          Number(cashAmount) === note
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        {currencySymbol}
                        {note}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* BANK / CARD MODE */}
            {paymentMethod === 'Bank' && (
              <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-700">Bank / Card Paid</label>
                  <button
                    type="button"
                    onClick={() => setBankAmount(String(roundMoney(totalAmount)))}
                    className="text-xs text-indigo-600 hover:text-indigo-700 font-semibold hover:underline"
                  >
                    Full Amount ({formatMoney(totalAmount)})
                  </button>
                </div>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-gray-400 font-semibold text-sm">
                    {currencySymbol}
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={bankAmount}
                    onChange={(e) => setBankAmount(e.target.value)}
                    placeholder="0.00"
                    disabled={isSubmitting}
                    className="w-full pl-9 pr-3 py-2 text-base font-bold text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-medium text-gray-500 block mb-1">
                    Card / Auth Ref (Optional)
                  </label>
                  <input
                    type="text"
                    value={referenceNo}
                    onChange={(e) => setReferenceNo(e.target.value)}
                    placeholder="e.g. Card Auth Code / Cheque #"
                    disabled={isSubmitting}
                    className="w-full px-3 py-1.5 text-xs text-gray-800 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            )}

            {/* SPLIT (CASH + CARD) MODE */}
            {paymentMethod === 'Split' && (
              <div className="space-y-3">
                {/* Cash portion */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                      <Banknote className="w-3.5 h-3.5 text-emerald-600" />
                      Cash Portion
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        const rem = Math.max(0, roundMoney(totalAmount - numBank));
                        setCashAmount(String(rem));
                      }}
                      className="text-[11px] text-indigo-600 hover:text-indigo-700 font-medium hover:underline"
                    >
                      Fill Remainder ({formatMoney(Math.max(0, totalAmount - numBank))})
                    </button>
                  </div>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-gray-400 font-semibold text-sm">
                      {currencySymbol}
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={cashAmount}
                      onChange={(e) => setCashAmount(e.target.value)}
                      placeholder="0.00"
                      disabled={isSubmitting}
                      className="w-full pl-9 pr-3 py-1.5 text-sm font-bold text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>

                {/* Bank / Card portion */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                      <CreditCard className="w-3.5 h-3.5 text-blue-600" />
                      Bank / Card Portion
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        const rem = Math.max(0, roundMoney(totalAmount - numCash));
                        setBankAmount(String(rem));
                      }}
                      className="text-[11px] text-indigo-600 hover:text-indigo-700 font-medium hover:underline"
                    >
                      Fill Remainder ({formatMoney(Math.max(0, totalAmount - numCash))})
                    </button>
                  </div>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-gray-400 font-semibold text-sm">
                      {currencySymbol}
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={bankAmount}
                      onChange={(e) => setBankAmount(e.target.value)}
                      placeholder="0.00"
                      disabled={isSubmitting}
                      className="w-full pl-9 pr-3 py-1.5 text-sm font-bold text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>

                {/* Optional Card Ref */}
                <div>
                  <label className="text-[11px] font-medium text-gray-500 block mb-1">
                    Card Ref / Auth (Optional)
                  </label>
                  <input
                    type="text"
                    value={referenceNo}
                    onChange={(e) => setReferenceNo(e.target.value)}
                    placeholder="e.g. Auth Code / Note"
                    disabled={isSubmitting}
                    className="w-full px-3 py-1.5 text-xs text-gray-800 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            )}

            {/* CREDIT (PAY LATER) MODE */}
            {paymentMethod === 'Credit' && (
              <div className="flex items-start gap-2.5 p-2 bg-indigo-50/70 border border-indigo-100 rounded-lg text-xs text-indigo-900">
                <Info className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold">Unpaid Invoice (Customer Credit)</div>
                  <div className="text-[11px] text-indigo-700 mt-0.5">
                    Invoice will be created with status <strong className="font-semibold">Unpaid</strong>. The full
                    balance of <strong>{formatMoney(totalAmount)}</strong> will be charged to customer{' '}
                    <strong>{customerName}</strong>'s credit ledger.
                  </div>
                </div>
              </div>
            )}

            {/* LIVE FINANCIAL SUMMARY BAR */}
            <div className="pt-2 border-t border-slate-200 flex flex-col gap-1.5 text-xs">
              <div className="flex items-center justify-between font-medium text-gray-600">
                <span>Amount Paid Now:</span>
                <span className={`font-bold ${totalPaid > 0 ? 'text-gray-900' : 'text-gray-500'}`}>
                  {formatMoney(totalPaid)}
                </span>
              </div>

              {/* Show Remaining Unpaid Balance if Partially Paid or Unpaid */}
              {remainingBalance > 0.005 && (
                <div className="flex items-center justify-between font-medium text-amber-800 bg-amber-50/80 px-2.5 py-1.5 rounded-lg border border-amber-200">
                  <span className="flex items-center gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                    <span>Remaining Balance (Due):</span>
                  </span>
                  <span className="font-extrabold text-amber-900">{formatMoney(remainingBalance)}</span>
                </div>
              )}

              {/* Show Cash Change if Paid more than total */}
              {cashChange > 0.005 && (
                <div className="flex items-center justify-between font-medium text-emerald-800 bg-emerald-50 px-2.5 py-1.5 rounded-lg border border-emerald-200">
                  <span className="flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>Change to Return:</span>
                  </span>
                  <span className="font-extrabold text-emerald-900">{formatMoney(cashChange)}</span>
                </div>
              )}

              {/* Status Badge */}
              <div className="pt-1 flex items-center justify-between">
                <span className="text-[11px] text-gray-500">Invoice Status:</span>
                {isFullyPaid ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800">
                    <CheckCircle className="w-3 h-3 text-emerald-600" />
                    Paid in full
                  </span>
                ) : isPartiallyPaid ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-800">
                    <AlertCircle className="w-3 h-3 text-amber-600" />
                    Partially Paid ({formatMoney(totalPaid)} / {formatMoney(totalAmount)})
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-slate-200 text-slate-700">
                    <Clock className="w-3 h-3 text-slate-500" />
                    Unpaid (Pay Later)
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Shift Note if available */}
          {openShift ? (
            <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 text-emerald-800 rounded-lg text-xs font-medium border border-emerald-100">
              <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>
                Active Shift: <strong className="font-semibold">{openShift.name}</strong>
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 text-amber-800 rounded-lg text-xs border border-amber-100">
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
              <span>No open shift detected. If shift tracking is required, please ensure a shift is open.</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirmSale}
            disabled={isSubmitting || isLoadingItems}
            className="inline-flex items-center justify-center gap-2 px-5 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 rounded-lg shadow-sm transition-colors disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Creating Invoice...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                {isFullyPaid && 'Complete Sale (Paid)'}
                {isPartiallyPaid && `Complete Sale (Partially Paid · ${formatMoney(totalPaid)})`}
                {isUnpaid && 'Complete Sale (Pay Later · Unpaid)'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
